import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { ChatAttachmentsApi } from '../apis/chat-attachments.api';
import { EnumAttachmentUploadState } from '../enums/attachment-upload-state.enum';
import { EnumAttachmentStatus } from '../enums/attachment-status.enum';
import { IAttachmentUpload } from '../interfaces/attachment-upload.interface';
import { IReusableAttachment } from '../interfaces/message-attachment.interface';
import {
  IUserAttachment,
  MAX_ATTACHMENTS_PER_MESSAGE
} from '../interfaces/user-attachment.interface';
import {
  attachmentErrorFromResponse,
  attachmentErrorMessage,
  isAttachmentErrorRetryable
} from '../utils/attachment-error-message.util';
import { generateLocalId } from '../utils/local-id.util';

export const POLL_INTERVAL_MS = 2000;
const MAX_POLL_FAILURES = 3;

const ACCEPTED_MIMES: ReadonlyArray<string> = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff'
];

const ACCEPTED_EXTENSIONS: ReadonlyArray<string> = [
  'pdf', 'txt', 'md', 'markdown', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff'
];

const isAcceptedFile = (file: File): boolean => {
  const mime = file.type?.toLowerCase().split(';')[0].trim();
  if (mime && ACCEPTED_MIMES.includes(mime)) {
    return true;
  }
  // Navegadores deixam o `type` vazio para .md e alguns .txt; a extensão decide aí.
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !mime && !!ext && ACCEPTED_EXTENSIONS.includes(ext);
};

export interface IAttachmentRejection {
  name: string;
  reason: string;
}

/**
 * Os anexos da mensagem em preparo. O arquivo sobe no gesto de anexar e o envio
 * fica travado até o servidor confirmar cada um.
 */
export class MessageAttachmentsStore {
  private readonly items$ = new BehaviorSubject<IAttachmentUpload[]>([]);
  private readonly polls = new Map<string, Subscription>();
  private readonly uploads = new Map<string, Subscription>();
  private readonly pollFailures = new Map<string, number>();
  private readonly fileByKey = new Map<string, File>();

  constructor(private api: ChatAttachmentsApi) {}

  getItems$(): Observable<IAttachmentUpload[]> {
    return this.items$.asObservable();
  }

  get items(): IAttachmentUpload[] {
    return this.items$.getValue();
  }

  get isEmpty(): boolean {
    return !this.items.length;
  }

  get attachments(): IReusableAttachment[] {
    return this.items
      .map((item) => item.attachment)
      .filter((a): a is IReusableAttachment => a !== null);
  }

  get remainingSlots(): number {
    return Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - this.items.length);
  }

  add(files: File[]): IAttachmentRejection[] {
    const rejections: IAttachmentRejection[] = [];
    const aceitos: File[] = [];

    for (const file of files) {
      if (!isAcceptedFile(file)) {
        rejections.push({ name: file.name, reason: 'Formato não aceito.' });
        continue;
      }
      if (aceitos.length >= this.remainingSlots) {
        rejections.push({
          name: file.name,
          reason: `Limite de ${MAX_ATTACHMENTS_PER_MESSAGE} arquivos por mensagem.`
        });
        continue;
      }
      aceitos.push(file);
    }

    const novos = aceitos.map((file) => {
      const key = generateLocalId();
      this.fileByKey.set(key, file);
      return {
        key,
        name: file.name,
        mime: file.type,
        sizeBytes: file.size,
        state: EnumAttachmentUploadState.UPLOADING,
        progress: 0,
        attachment: null,
        errorMessage: null,
        errorRetryable: true
      };
    });

    if (!novos.length) {
      return rejections;
    }
    this.items$.next([...this.items, ...novos]);
    // O arquivo sobe no gesto de anexar: o anexo não tem vínculo com a conversa,
    // que só o referencia por id no envio da mensagem.
    novos.forEach((item) =>
      this.startUpload(item.key, this.fileByKey.get(item.key))
    );
    return rejections;
  }

  get hasPendingUpload(): boolean {
    return this.items.some(
      (item) => item.state !== EnumAttachmentUploadState.READY
    );
  }

  remove(key: string): void {
    const item = this.items.find((i) => i.key === key);
    this.stopWork(key);
    this.fileByKey.delete(key);
    this.items$.next(this.items.filter((i) => i.key !== key));
    if (item?.attachment) {
      this.api.delete(item.attachment.attachmentId).subscribe({
        error: () => undefined
      });
    }
  }

  discard(): void {
    this.items.forEach((item) => this.remove(item.key));
  }

  clear(): void {
    this.items.forEach((item) => this.stopWork(item.key));
    this.fileByKey.clear();
    this.items$.next([]);
  }

  dispose(): void {
    this.clear();
  }

  private startUpload(key: string, file: File | undefined): void {
    if (!file) {
      this.fail(key);
      return;
    }
    this.uploads.get(key)?.unsubscribe();
    this.uploads.set(
      key,
      this.api.upload({ file, idempotencyKey: key }).subscribe({
        next: (event) => {
          this.patch(key, { progress: event.progress });
          if (event.attachment) {
            this.applyAttachment(key, event.attachment);
          }
        },
        error: (error) =>
          this.fail(
            key,
            attachmentErrorFromResponse(error),
            isAttachmentErrorRetryable(error)
          )
      })
    );
  }

  private applyAttachment(key: string, attachment: IUserAttachment): void {
    if (!this.items.some((i) => i.key === key)) {
      return;
    }
    const state = this.stateFor(attachment);
    this.patch(key, {
      attachment,
      state,
      progress: 100,
      errorMessage:
        state === EnumAttachmentUploadState.FAILED
          ? attachmentErrorMessage(attachment.errorCode, attachment.errorMessage)
          : null
    });
    if (state === EnumAttachmentUploadState.PROCESSING) {
      this.startPolling(key, attachment.attachmentId);
      return;
    }
    this.stopPolling(key);
  }

  private stateFor(attachment: IUserAttachment): EnumAttachmentUploadState {
    switch (attachment.status) {
      case EnumAttachmentStatus.Ready:
        return EnumAttachmentUploadState.READY;
      case EnumAttachmentStatus.Failed:
      case EnumAttachmentStatus.Deleted:
        return EnumAttachmentUploadState.FAILED;
      default:
        return EnumAttachmentUploadState.PROCESSING;
    }
  }

  private startPolling(key: string, attachmentId: string): void {
    this.stopPolling(key);
    this.pollFailures.set(key, 0);
    this.polls.set(
      key,
      timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).subscribe(() =>
        this.api.getById(attachmentId).subscribe({
          next: (attachment) => {
            this.pollFailures.set(key, 0);
            this.applyAttachment(key, attachment);
          },
          error: (error) => this.onPollError(key, error)
        })
      )
    );
  }

  private onPollError(key: string, error: { status?: number }): void {
    const failures = (this.pollFailures.get(key) ?? 0) + 1;
    this.pollFailures.set(key, failures);
    // 404/410 são veredito do servidor; o resto pode ser rede e merece reconsulta.
    const definitivo = error?.status === 404 || error?.status === 410;
    if (definitivo || failures >= MAX_POLL_FAILURES) {
      this.stopPolling(key);
      this.patch(key, {
        state: EnumAttachmentUploadState.FAILED,
        errorMessage: definitivo
          ? 'Este arquivo não está mais disponível.'
          : 'Não foi possível confirmar o processamento do arquivo.'
      });
    }
  }

  private fail(key: string, message?: string, errorRetryable = true): void {
    this.stopPolling(key);
    this.patch(key, {
      state: EnumAttachmentUploadState.FAILED,
      errorMessage: message ?? attachmentErrorMessage(null),
      errorRetryable
    });
  }

  private stopPolling(key: string): void {
    this.polls.get(key)?.unsubscribe();
    this.polls.delete(key);
    this.pollFailures.delete(key);
  }

  private stopWork(key: string): void {
    this.stopPolling(key);
    this.uploads.get(key)?.unsubscribe();
    this.uploads.delete(key);
  }

  private patch(key: string, update: Partial<IAttachmentUpload>): void {
    const items = this.items;
    const index = items.findIndex((i) => i.key === key);
    if (index === -1) {
      return;
    }
    const atualizada = [...items];
    atualizada[index] = { ...items[index], ...update };
    this.items$.next(atualizada);
  }
}
